import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import {
    entityStateZodSchema,
    type EntityState,
} from "@@/shared/entity-status";
import { addressZodSchema, type Address } from "@@/shared/locations/address";
import type { Except, MergeExclusive } from "type-fest";
import { z } from "zod";
import { deliveryMethodType } from "../delivery-methods";
import {
    pickUpDeliveryIdFactory,
    pickUpDeliveryIdZodSchema,
    type PickUpDeliveryId,
} from "./pick-up-delivery-id";
import {
    pickUpDestinationZodSchema,
    type PickUpDestination,
} from "./pick-up-destination";
import { pickUpOriginZodSchema, type PickUpOrigin } from "./pick-up-origin";

type PickUpDelivery = {
    readonly _id: PickUpDeliveryId;
    readonly _type: "PICK_UP_DELIVERY";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly destination: PickUpDestination;
    readonly origin: PickUpOrigin;
    readonly pickUpAddress: Address;
    readonly pickUpInstructions?: string | null;
    readonly estimatedDeliveryTime?: IsoAndUnixTimestamp;
    readonly requestedDeliveryTime?: MergeExclusive<
        IsoAndUnixTimestamp,
        "ASAP"
    >;
    readonly deliveryMethodType: typeof deliveryMethodType.PICK_UP;
};

type CreatePickUpDeliveryParams = Except<
    PickUpDelivery,
    "_createdAt" | "_id" | "_status" | "_type" | "deliveryMethodType"
> & {
    _id?: PickUpDeliveryId;
};

const pickUpDeliveryFactory = (
    params: CreatePickUpDeliveryParams,
): PickUpDelivery => {
    return {
        ...params,
        _id: params._id ?? pickUpDeliveryIdFactory(),
        _type: "PICK_UP_DELIVERY",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
        deliveryMethodType: deliveryMethodType.PICK_UP,
    };
};

const pickUpDeliveryZodSchema: z.ZodSchema<PickUpDelivery> = z.object({
    _id: pickUpDeliveryIdZodSchema,
    _type: z.literal("PICK_UP_DELIVERY"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    pickUpAddress: addressZodSchema,
    pickUpInstruction: z.string().nullish(),
    estimatedDeliveryTime: isoAndUnixZodSchema.optional(),
    requestedDeliveryTime: z
        .union([isoAndUnixZodSchema, z.literal("ASAP")])
        .optional(),
    deliveryMethodType: z.literal(deliveryMethodType.PICK_UP),
    destination: pickUpDestinationZodSchema,
    origin: pickUpOriginZodSchema,
});

export { pickUpDeliveryFactory, pickUpDeliveryZodSchema };
export type { PickUpDelivery };
